import { Box, Button, HStack, VStack } from "@chakra-ui/react";
import { faBell, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import { Badge } from "antd";
import useDeviceNotifications, { Notification } from "hooks/useDeviceNotifications";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import parseHTML from "html-react-parser";
import { FC, useEffect, useState } from "react";

const NotificationsDropdown: FC = () => {
    const [page, setPage] = useState(1);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const { data: currentDataPage, isLoading } = useDeviceNotifications(true, page);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!isLoading && currentDataPage) {
            const newData = [...(currentDataPage.data ?? [])];
            setNotifications(cur => [...cur, ...newData]);
        }
    }, [currentDataPage, isLoading]);

    const update = useAuthenticatedRequestCreator<any, any>(() => ({
        method: "post",
        url: "/device/notifications/seen/all",
    }));

    const { mutate: markAllAsSeen, isLoading: markingAllAsSeen } =
        useMutation(update);

    const notificationsScrolled = (evt: any) => {
        if (currentDataPage && page >= currentDataPage.pageCount) {
            // we've loaded it all
            return;
        }

        const isAtBottom = Math.abs(
            evt.target.scrollHeight -
            evt.target.clientHeight -
            evt.target.scrollTop
        ) <= 1;

        if (isAtBottom) {
            setPage(p => p + 1);
        }
    }

    return (
        <Box position="relative">
            <Badge count={currentDataPage?.extra.unreadCount}>
                <Box
                    bgColor="black"
                    rounded="full"
                    padding="3"
                    cursor="pointer"
                    _hover={{
                        outline: "2px solid var(--dcs-blue)",
                    }}
                    as="button"
                    onClick={() => setExpanded((prevState) => !prevState)}
                >
                    {!isLoading && <FontAwesomeIcon icon={faBell} color="white" size="xl" />}
                    {isLoading && <FontAwesomeIcon icon={faSpinner} color="white" size="xl" spin />}
                </Box>
            </Badge>
            {expanded && (
                <Box
                    position="absolute"
                    border="1px solid gray"
                    rounded="md"
                    bgColor="black"
                    top={14}
                    right={0}
                    padding={2}
                    minWidth={360}
                    zIndex={10}
                >
                    <HStack alignItems="center" justifyContent="center">
                        <Button
                            textAlign="center"
                            color="white"
                            fontWeight="bold"
                            variant="link"
                            onClick={() => {
                                markAllAsSeen({});
                            }}
                            disabled={markingAllAsSeen}
                        >
                            Mark all as seen
                        </Button>
                    </HStack>
                    <Box maxHeight="50vh" width="100%" overflow="scroll" onScroll={notificationsScrolled}>
                        {notifications ? (
                            notifications.map((t, i) => {
                                const createdAt = new Date(t.timestamp);
                                const createdAtDate =
                                    createdAt.toLocaleDateString();

                                return (
                                    <HStack
                                        color="white"
                                        padding={2}
                                        paddingBottom={4}
                                        marginX={2}
                                        borderBottom={
                                            i !== notifications.length - 1
                                                ? "1px solid white"
                                                : ""
                                        }
                                        key={t.id}
                                        width="calc(100% - 1rem)"
                                    >
                                        {!t.seen && (
                                            <Box minWidth=".5rem">
                                                <Badge dot={!t.seen} />
                                            </Box>
                                        )}
                                        <VStack
                                            alignItems="start"
                                            flex={1}
                                            width="100%"
                                        >
                                            <HStack
                                                width="100%"
                                                justify="space-between"
                                                mb={2}
                                            >
                                                <Box fontWeight="bold">
                                                    {t.title}
                                                </Box>
                                                <Box>{createdAtDate}</Box>
                                            </HStack>
                                            {t.body.html ? (
                                                <Box>
                                                    {parseHTML(t.body.html)}
                                                </Box>
                                            ) : (
                                                <Box>{t.body.text}</Box>
                                            )}
                                        </VStack>
                                    </HStack>
                                );
                            })
                        ) : (
                            <Box> No notifications! </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default NotificationsDropdown;
